import React from 'react';
import { privateLocationTitle, privateLocationSubtitle } from './event-date-utils';

/**
 * Private-location badge, minimal, single-line indicator that the venue is
 * revealed only after confirmation. On hover the letters bob up in sequence,
 * like a crowd/Mexican wave rippling from the pin outward.
 *
 * The wave triggers on the nearest `group` hover, on cards that's the whole
 * card; the "card" variant makes its own wrapper a group so hovering the badge
 * works on its own too.
 *
 * variant "pill" → compact chip for cards; variant "card" → title + subtitle.
 */
const PinIcon = ({ className = 'h-3.5 w-3.5' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 21s7-5.686 7-11a7 7 0 1 0-14 0c0 5.314 7 11 7 11z" />
        <circle cx="12" cy="10" r="2.5" />
    </svg>
);

// Letters bob up in sequence on group-hover, the "crowd wave". The per-letter
// animation-delay (inline) staggers the ripple from the first character.
const WaveLetters = ({ text, className = '' }) => (
    <span className={`inline-flex ${className}`} aria-label={text}>
        {Array.from(text).map((ch, i) => (
            <span
                key={i}
                aria-hidden="true"
                className="inline-block group-hover:animate-crowd-wave"
                style={{ animationDelay: `${i * 0.045}s` }}
            >
                {ch === ' ' ? '\u00A0' : ch}
            </span>
        ))}
    </span>
);

export default function PrivateLocationBadge({ lang = 'he', variant = 'card', className = '' }) {
    const title = privateLocationTitle(lang);
    const subtitle = privateLocationSubtitle(lang);

    if (variant === 'pill') {
        return (
            <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-secondary-300 text-sm font-bold font-primary ${className}`}>
                <PinIcon className="h-4 w-4 shrink-0" />
                <WaveLetters text={title} />
            </span>
        );
    }

    return (
        <span className={`group inline-flex items-start gap-2 ${className}`}>
            <PinIcon className="h-5 w-5 shrink-0 mt-0.5 text-secondary-300" />
            <span className="flex flex-col text-start leading-tight">
                <WaveLetters text={title} className="text-secondary-300 text-base md:text-lg font-bold font-primary" />
                <span className="text-white/45 text-sm font-primary">{subtitle}</span>
            </span>
        </span>
    );
}
